import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, startWith } from 'rxjs';

import { ContractType } from '@features/termination/enums';
import { TerminationType } from '@features/termination/enums/termination-list.enum';
import { TerminationInformation } from '@features/termination/interfaces';
import { Product } from '@features/termination/models';
import { OTHER } from '@shared/utils';

import { TerminationInformationForm } from '../../forms';
import * as fromStore from '../../store';
import { TerminationFacade } from '../../termination.facade';

@UntilDestroy()
@Injectable()
export class TerminationInformationFacade {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  private readonly store: Store = inject(Store);

  private readonly terminationFacade: TerminationFacade = inject(TerminationFacade);

  public bankingProductValue: ContractType = ContractType.BANKING_PRODUCT;

  public insuranceValue: ContractType = ContractType.INSURANCE;

  public withdrawValue: TerminationType = TerminationType.CANCEL;

  public terminationInformationForm: FormGroup<TerminationInformationForm> =
    this.formBuilder.group<TerminationInformationForm>({
      contractType: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      terminationType: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      contract: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      contractName: new FormControl('', { nonNullable: true }),
      contractNumber: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      terminationDate: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      terminationReason: new FormControl('', { nonNullable: true }),
      otherReason: new FormControl('', { nonNullable: true }),
    });

  public groundsList$: Observable<string[]> = this.store.select(fromStore.selectGroundsList);

  public bankingProducts$: Observable<Product[]> = this.store.select(
    fromStore.selectBankingProducts,
  );

  public insurances$: Observable<Product[]> = this.store.select(fromStore.selectInsurances);

  constructor() {
    this.changeTerminationTypeValue();
    this.registerContractTypeFieldValidators();
    this.registerContractFieldValidators();
    this.registerTerminationReasonFieldValidators();
  }

  private changeTerminationTypeValue(): void {
    this.contractType.valueChanges.pipe(untilDestroyed(this)).subscribe((contractType) => {
      if (contractType === ContractType.BANKING_PRODUCT) {
        this.withdrawValue = TerminationType.CANCEL;
        this.terminationType.setValue(TerminationType.CANCEL);
        this.terminationType.updateValueAndValidity();
      }

      if (contractType === ContractType.INSURANCE) {
        this.withdrawValue = TerminationType.WITHDRAW;
        this.terminationType.setValue(TerminationType.WITHDRAW);
        this.terminationType.updateValueAndValidity();
      }
    });
  }

  private registerContractTypeFieldValidators(): void {
    combineLatest([
      this.contractType.valueChanges.pipe(startWith(this.contractType.value)),
      this.terminationType.valueChanges.pipe(startWith(this.terminationType.value)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([contractType, terminationType]) => {
        if (contractType === ContractType.BANKING_PRODUCT) {
          this.terminationDate.setValue(new Date().toLocaleDateString('fr-FR'));
          this.terminationDate.disable();
          this.terminationReason.reset();
          this.terminationReason.removeValidators(Validators.required);
        }

        if (contractType === ContractType.INSURANCE) {
          this.terminationDate.removeValidators(Validators.required);
          this.terminationReason.removeValidators(Validators.required);
          this.terminationDate.reset();
          if (terminationType === TerminationType.TERMINATE) {
            this.terminationDate.addValidators(Validators.required);
            this.terminationReason.reset();
            this.terminationReason.addValidators(Validators.required);
          }
        }

        this.contract.reset(null);
        this.contractName.reset();
        this.contractNumber.reset();
        this.terminationReason.reset();
        this.otherReason.reset();

        this.terminationDate.updateValueAndValidity();
      });
  }

  private registerContractFieldValidators(): void {
    this.contract.valueChanges.pipe(untilDestroyed(this)).subscribe((contract) => {
      if (contract === OTHER) {
        this.contractName.addValidators(Validators.required);
      } else {
        this.contractName.reset();
        this.contractName.removeValidators(Validators.required);
      }

      this.contractName.updateValueAndValidity();
    });
  }

  private registerTerminationReasonFieldValidators(): void {
    this.terminationReason.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((terminationReason) => {
        if (terminationReason === OTHER) {
          this.otherReason.addValidators(Validators.required);
        } else {
          this.otherReason.reset();
          this.otherReason.removeValidators(Validators.required);
        }

        this.otherReason.updateValueAndValidity();
      });
  }

  public get contractType(): FormControl<string | null> {
    return this.terminationInformationForm.controls.contractType;
  }

  public get terminationType(): FormControl<string | null> {
    return this.terminationInformationForm.controls.terminationType;
  }

  public get contract(): FormControl<string | null> {
    return this.terminationInformationForm.controls.contract;
  }

  public get contractName(): FormControl<string | null> {
    return this.terminationInformationForm.controls.contractName;
  }

  public get contractNumber(): FormControl<string | null> {
    return this.terminationInformationForm.controls.contractNumber;
  }

  public get terminationDate(): FormControl<string | null> {
    return this.terminationInformationForm.controls.terminationDate;
  }

  public get terminationReason(): FormControl<string | null> {
    return this.terminationInformationForm.controls.terminationReason;
  }

  public get otherReason(): FormControl<string | null> {
    return this.terminationInformationForm.controls.otherReason;
  }

  public setTerminationInformationFormDefaultValues(): void {
    this.terminationFacade.terminationInformation$
      .pipe(untilDestroyed(this))
      .subscribe((terminationInformation: TerminationInformation) => {
        this.terminationInformationForm.reset(terminationInformation);
      });
  }

  public saveTerminationInformation(): void {
    const terminationInformationToSave: TerminationInformation =
      this.terminationInformationForm.getRawValue();
    console.log(terminationInformationToSave);

    this.store.dispatch(
      fromStore.TerminationInformationSave({
        terminationInformation: terminationInformationToSave,
      }),
    );
  }
}
